// ConfigTab.js
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { fetchConfigData, baseURL, dateTemplate } from './DataFlow';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';

const ConfigTab = () => {
    const [testConfig, setTestConfig] = useState([]);
    const [schedulerConfig, setSchedulerConfig] = useState([]);
    const [selectedTestConfig, setSelectedTestConfig] = useState([]);
    const [selectedSchedulerConfig, setSelectedSchedulerConfig] = useState([]);
    const [editingRowData, setEditingRowData] = useState({});
    const toast = useRef(null);

    useEffect(() => {
        fetchConfigData(setTestConfig, setSchedulerConfig);
    }, []);

    // Determine if the user is an admin
    const isAdmin = localStorage.getItem('isAppPowerUser') === 'true';

    const showErrorMessages = (messages) => {
        if (toast.current) {
            messages.forEach((msg) => {
                toast.current.show({ severity: 'error', summary: 'Validation Error', detail: msg, life: 3000 });
            });
        }
    };

    const isFieldValid = (field, value) => {
        if (field === 'startTime') {
            return !!value;
        }
        if (field === 'branch' || field === 'testType') {
            return value && value.trim() !== '';
        }
        return true;
    };

    // Unique ID generator
    const createId = () => {
        return '_' + Math.random().toString(36).substr(2, 9);
    };

    // Options for dropdowns
    const projectOptions = [
        { label: 'LL', value: 'LL' },
        { label: 'MFC', value: 'MFC' },
        { label: 'CC', value: 'CC' },
        { label: 'HOV', value: 'HOV' },
    ];

    const periodOptions = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
    ];

    const dayOfWeekOptions = [
        { label: 'Sunday', value: 'Sunday' },
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
    ];

    // Options for platforms
    const platformOptions = [
        { label: 'Both', value: 'both' },
        { label: 'Android', value: 'android' },
        { label: 'iOS', value: 'ios' },
    ];

    // Common editor functions
    const textEditor = (options) => {
        const isValid = isFieldValid(options.field, options.value);
        return (
            <div>
                <InputText
                    value={options.value || ''}
                    onChange={(e) => {
                        options.editorCallback(e.target.value);
                    }}
                    className={classNames({ 'p-invalid': !isValid })}
                />
            </div>
        );
    };

    const numberEditor = (options) => (
        <InputNumber
            value={options.value || 0}
            onValueChange={(e) => {
                options.editorCallback(e.value);
                updateEditingRowData(options.field, e.value);
            }}
        />
    );

    const dropdownEditor = (options, dropdownOptions) => (
        <Dropdown
            value={options.value}
            options={dropdownOptions}
            onChange={(e) => {
                options.editorCallback(e.value);
                updateEditingRowData(options.field, e.value);
            }}
            placeholder="Select"
        />
    );

    const timeEditor = (options) => {
        const isValid = isFieldValid(options.field, options.value);
        return (
            <div>
                <Calendar
                    value={options.value || null}
                    onChange={(e) => {
                        options.editorCallback(e.value);
                    }}
                    timeOnly
                    hourFormat="24"
                    className={classNames({ 'p-invalid': !isValid })}
                />
            </div>
        );
    };

    const checkboxEditor = (options) => (
        <Checkbox
            checked={options.value || false}
            onChange={(e) => {
                options.editorCallback(e.checked);
                updateEditingRowData(options.field, e.checked);
            }}
        />
    );

    // Function to update editing row data
    const updateEditingRowData = (field, value) => {
        setEditingRowData((prevData) => {
            const updatedData = { ...prevData, [field]: value };

            if (field === 'period') {
                const periodValue = value.toLowerCase();
                if (periodValue === 'weekly') {
                    // Check if 'startDay' is a valid day of the week
                    const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
                    if (!validDaysOfWeek.includes(updatedData.startDay)) {
                        // 'startDay' is invalid, set to 'Monday'
                        updatedData.startDay = 'Monday';
                    }
                } else if (periodValue === 'monthly') {
                    // For 'Monthly' period, ensure 'startDay' is a valid number between 1 and 31
                    let day = parseInt(updatedData.startDay, 10);
                    if (isNaN(day) || day < 1 || day > 31) {
                        day = 1;
                    }
                    updatedData.startDay = day.toString();
                }
            }

            return updatedData;
        });
    };

    // Add new row functions
    const addSchedulerConfigRow = async () => {
        const newItem = {
            // Do not assign an ID; let the backend assign it
            gameName: '',
            period: '',
            startDay: '', // Initialize startDay
            startTime: null,
            branch: '',
            testType: '',
            lastExecutionDate: null,
            enabled: false,
            awaitingIOS: false,
            awaitingAndroid: false,
            platform: 'both', // Default value
        };

        try {
            const authToken = localStorage.getItem('apiToken');
            const response = await fetch(baseURL + '/api/config/scheduler-config', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'api_token': authToken
                },
                body: JSON.stringify(newItem),
            });

            if (!response.ok) {
                throw new Error('Failed to create a new scheduler configuration.');
            }

            const createdItem = await response.json();
            setSchedulerConfig([...schedulerConfig, createdItem]);
        } catch (error) {
            console.error('Error creating scheduler configuration:', error);
            // Handle error state here if necessary
        }
    };

    // Delete selected rows functions
    const deleteTestConfigRows = async () => {
        const idsToDelete = selectedTestConfig.map(item => item._id);

        const authToken = localStorage.getItem('apiToken');
        try {
            await Promise.all(
                idsToDelete.map(id =>
                    fetch(`/api/config/test-config/${id}`, {
                        method: 'DELETE',
                        headers: {
                            'api_token': authToken,
                        },
                    })
                )
            );

            setTestConfig(testConfig.filter(row => !idsToDelete.includes(row._id)));
            setSelectedTestConfig([]);
        } catch (error) {
            console.error('Error deleting test configurations:', error);
            // Handle error state here if necessary
        }
    };

    const deleteSchedulerConfigRows = async () => {
        const idsToDelete = selectedSchedulerConfig.map(item => item._id);
        const authToken = localStorage.getItem('apiToken');

        try {
            await Promise.all(
                idsToDelete.map(id =>
                    fetch(`/api/config/scheduler-config/${id}`, {
                        method: 'DELETE',
                        headers: {
                            'api_token': authToken,
                        },
                    })
                )
            );

            setSchedulerConfig(schedulerConfig.filter(row => !idsToDelete.includes(row._id)));
            setSelectedSchedulerConfig([]);
        } catch (error) {
            console.error('Error deleting scheduler configurations:', error);
            // Handle error state here if necessary
        }
    };

    const onTestConfigRowEditComplete = async (e) => {
        let updatedRows = [...testConfig];
        updatedRows[e.index] = e.newData;
        setTestConfig(updatedRows);

        try {
            const authToken = localStorage.getItem('apiToken');
            const response = await fetch(`/api/config/test-config/${e.newData._id}`, {
                method: 'PUT',
                headers: {
                    'api_token': authToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(e.newData),
            });

            if (!response.ok) {
                throw new Error('Failed to update the test configuration.');
            }
        } catch (error) {
            console.error('Error updating test configuration:', error);
            // Optionally revert changes or display an error message
        }
    };

    const onSchedulerRowEditComplete = async (e) => {
        let updatedData = { ...e.newData };

        // Ensure period value is lowercase
        if (updatedData.period) {
            updatedData.period = updatedData.period.toLowerCase();
        }

        // Validate required fields
        const validationErrors = [];

        // Validate 'startTime'
        if (!updatedData.startTime) {
            validationErrors.push('Start Time is required.');
        }

        // Validate 'branch'
        if (!updatedData.branch || updatedData.branch.trim() === '') {
            validationErrors.push('Branch Pattern is required.');
        }

        // Validate 'testType'
        if (!updatedData.testType || updatedData.testType.trim() === '') {
            validationErrors.push('Test Type is required.');
        }

        // If there are validation errors, display them and prevent saving
        if (validationErrors.length > 0) {
            showErrorMessages(validationErrors);
            // Keep the row in edit mode
            return;
        }

        // Existing logic to handle 'startDay' based on 'period'
        if (updatedData.period === 'monthly') {
            // Ensure 'startDay' is a valid number between 1 and 31
            let day = parseInt(updatedData.startDay, 10);
            if (isNaN(day) || day < 1 || day > 31) {
                day = 1;
            }
            updatedData.startDay = day.toString();
        } else if (updatedData.period === 'weekly') {
            // Ensure 'startDay' is a valid day of the week
            const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
            if (!validDaysOfWeek.includes(updatedData.startDay)) {
                updatedData.startDay = 'Monday';
            }
        }

        let updatedRows = [...schedulerConfig];
        updatedRows[e.index] = updatedData;
        setSchedulerConfig(updatedRows);
        setEditingRowData({});

        try {
            const authToken = localStorage.getItem('apiToken');
            const response = await fetch(`/api/config/scheduler-config/${updatedData._id}`, {
                method: 'PUT',
                headers: {
                    'api_token': authToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData),
            });

            if (!response.ok) {
                throw new Error('Failed to update the scheduler configuration.');
            }
        } catch (error) {
            console.error('Error updating scheduler configuration:', error);
            // Optionally handle the error
        }
    };

    const onSchedulerRowEditInit = (e) => {
        setEditingRowData({ ...e.data });
    };

    const onSchedulerRowEditCancel = () => {
        setEditingRowData({});
    };

    // Conditional field rendering for Scheduler Config
    const renderConditionalField = (rowData, field) => {
        const isEditing = editingRowData._id === rowData._id;
        const period = (isEditing ? editingRowData.period : rowData.period)?.toLowerCase();
        const data = isEditing ? editingRowData : rowData;

        if (field === 'startDay') {
            switch (period) {
                case 'weekly':
                    return data.startDay; // Should be a day of the week
                case 'monthly':
                    return data.startDay; // Should be a day of the month (e.g., "1st", "15th")
                default:
                    return '';
            }
        }

        if (field === 'startTime' && period) {
            if (data.startTime) {
                const time = new Date(data.startTime);
                if (!isNaN(time)) {
                    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }
            }
        }

        return '';
    };

    const conditionalEditor = (options, field) => {
        const isEditing = editingRowData._id === options.rowData._id;
        const period = (isEditing ? editingRowData.period : options.rowData.period)?.toLowerCase();

        if (field === 'startDay') {
            if (period === 'weekly') {
                // Ensure 'startDay' is a valid day of the week
                const validDaysOfWeek = dayOfWeekOptions.map(option => option.value);
                let value = options.value;
                if (!validDaysOfWeek.includes(value)) {
                    value = 'Monday';
                    options.editorCallback(value);
                    updateEditingRowData(options.field, value);
                }
                // Dropdown for days of the week
                return (
                    <Dropdown
                        value={value}
                        options={dayOfWeekOptions}
                        onChange={(e) => {
                            options.editorCallback(e.value);
                            updateEditingRowData(options.field, e.value);
                        }}
                        placeholder="Select Day"
                    />
                );
            }
            if (period === 'monthly') {
                // InputNumber for day of the month (1-31)
                let value = parseInt(options.value, 10);
                if (isNaN(value) || value < 1 || value > 31) {
                    value = 1;
                    options.editorCallback(value);
                    updateEditingRowData(options.field, value);
                }
                return (
                    <InputNumber
                        value={value}
                        onValueChange={(e) => {
                            options.editorCallback(e.value);
                            updateEditingRowData(options.field, e.value);
                        }}
                        min={1}
                        max={31}
                        placeholder="Enter day (1-31)"
                    />
                );
            }
        }

        if (field === 'startTime' && period) {
            return timeEditor(options);
        }

        return null;
    };

    const addTestConfigRow = async () => {
        const newItem = {
            id: createId(),
            gameName: '',
            branchPattern: '',
            testSuites: '',
            priority: 0,
            enabled: false,
            platform: 'both', // Default value
        };

        try {
            const authToken = localStorage.getItem('apiToken');

            const response = await fetch(baseURL + '/api/config/test-config', {
                method: 'POST',
                headers: {
                    'api_token': authToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newItem),
            });

            if (!response.ok) {
                throw new Error('Failed to create a new test configuration.');
            }

            const createdItem = await response.json();
            setTestConfig([...testConfig, createdItem]);
        } catch (error) {
            console.error('Error creating test configuration:', error);
            // Handle error state here if necessary
        }
    };

    // Function to assign a class to rows based on the 'enabled' field
    const rowClassName = (rowData) => {
        return !rowData.enabled ? 'disabled-row' : '';
    };

    return (
        <div>
            <Toast ref={toast} />
            {/* Config Test Table */}
            <h2>Config Test</h2>
            {isAdmin && (
                 <div className="p-mb-2 button-container" style={{ width: '15%', paddingBottom:'10px' }}>
                    <Button label="Config" icon="pi pi-plus" onClick={addTestConfigRow} className="p-button-success p-mr-2" />
                    <Button
                        label="Delete"
                        icon="pi pi-trash"
                        onClick={deleteTestConfigRows}
                        className="p-button-danger"
                        disabled={!selectedTestConfig.length}
                    />
                </div>
            )}
            <DataTable
                value={testConfig}
                editMode={isAdmin ? 'row' : undefined}
                dataKey="_id"
                selection={isAdmin ? selectedTestConfig : null}
                onSelectionChange={isAdmin ? (e) => setSelectedTestConfig(e.value) : null}
                selectionMode={isAdmin ? 'checkbox' : null}
                paginator
                rows={10}
                onRowEditComplete={onTestConfigRowEditComplete}
                showGridlines
                rowClassName={rowClassName}
            >
                {isAdmin && <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />}
                <Column field="gameName" header="Project" editor={isAdmin ? (options) => dropdownEditor(options, projectOptions) : null} />
                <Column field="branchPattern" header="Branch Pattern" editor={isAdmin ? textEditor : null} />
                <Column field="testSuites" header="Test Suites" editor={isAdmin ? textEditor : null} />
                <Column field="priority" header="Priority" editor={isAdmin ? numberEditor : null} />
                <Column field="platform" header="Platform" editor={isAdmin ? (options) => dropdownEditor(options, platformOptions) : null} />
                <Column field="enabled" header="Enabled" editor={isAdmin ? checkboxEditor : null} />
                {isAdmin && (
                    <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }} />
                )}
            </DataTable>

            {/* Config Scheduler Table */}
            <h2>Config Scheduler</h2>
            {isAdmin && (
                <div className="p-mb-2 button-container" style={{ width: '15%', paddingBottom:'10px' }}>
                    <Button label="Schedule" icon="pi pi-plus" onClick={addSchedulerConfigRow} className="p-button-success p-mr-2" />
                    <Button
                        label="Delete"
                        icon="pi pi-trash"
                        onClick={deleteSchedulerConfigRows}
                        className="p-button-danger"
                        disabled={!selectedSchedulerConfig.length}
                    />
                </div>
            )}
            <DataTable
                value={schedulerConfig}
                editMode={isAdmin ? 'row' : undefined}
                dataKey="_id"
                selection={isAdmin ? selectedSchedulerConfig : null}
                onSelectionChange={isAdmin ? (e) => setSelectedSchedulerConfig(e.value) : null}
                selectionMode={isAdmin ? 'checkbox' : null}
                paginator
                rows={10}
                showGridlines
                onRowEditInit={isAdmin ? onSchedulerRowEditInit : null}
                onRowEditCancel={isAdmin ? onSchedulerRowEditCancel : null}
                onRowEditComplete={isAdmin ? onSchedulerRowEditComplete : null}
                rowClassName={rowClassName} // Assign class based on 'enabled' field
            >
                {isAdmin && <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />}
                <Column field="gameName" header="Project" editor={isAdmin ? (options) => dropdownEditor(options, projectOptions) : null} />
                <Column field="period" header="Period" editor={isAdmin ? (options) => dropdownEditor(options, periodOptions) : null} />
                {/* Conditional Fields */}
                <Column
                    field="startDay"
                    header="Start Day"
                    body={(rowData) => renderConditionalField(rowData, 'startDay')}
                    editor={isAdmin ? (options) => conditionalEditor(options, 'startDay') : null}
                />
                <Column
                    field="startTime"
                    header="Start Time"
                    body={(rowData) => renderConditionalField(rowData, 'startTime')}
                    editor={isAdmin ? (options) => conditionalEditor(options, 'startTime') : null}
                />
                <Column field="branch" header="Branch Pattern" editor={isAdmin ? textEditor : null} />
                <Column field="testType" header="Test Type" editor={isAdmin ? textEditor : null} />
                {/* Non-Editable Fields */}
                <Column
                    field="lastExecutionDate"
                    header="Last Execution Date"
                    body={dateTemplate}
                />
                <Column field="enabled" header="Enabled" editor={isAdmin ? checkboxEditor : null} />
                <Column field="awaitingIOS" header="Awaiting iOS Build" />
                <Column field="awaitingAndroid" header="Awaiting Android Build" />
                <Column field="platform" header="Platform" editor={isAdmin ? (options) => dropdownEditor(options, platformOptions) : null} />
                {isAdmin && (
                    <Column header="Actions" rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }} />
                )}
            </DataTable>
        </div>
    );
};

export default ConfigTab;
